/*
  NOTE: I probably shouldn't use this in most cases. It's a legacy thing. For new scenarios, I should use `VideoGif` unless I only have a GIF and can't convert to video.
*/
'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import { Pause, Play } from 'lucide-react';

import type { SharedProps } from './ContentImage.types';
import { BasicImage } from './ContentImage.helpers';

interface Props extends SharedProps {
  src: string;
  coverSrc: string;
}

function ToggleableGifImage({ src, coverSrc, ...delegated }: Props) {
  const [isPaused, setIsPaused] = React.useState<boolean>(false);
  const [showControls, setShowControls] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    // If the user prefers reduced motion and a cover image is provided, we’ll render the cover image by default.
    const prefersReducedMotion = !window.matchMedia(
      '(prefers-reduced-motion: no-preference)'
    ).matches;

    if (prefersReducedMotion) {
      setIsPaused(true);
      setShowControls(true);
    }
  }, [coverSrc]);

  function handleTogglePlaying(method: 'pointer' | 'keyboard') {
    if (isPaused) {
      setIsPaused(false);

      // Clicking the play button should hide the controls, but we shouldn't do this for keyboard users, since it would be strange to have the currently-focused element become invisible.
      if (method === 'pointer') {
        setShowControls(false);
      }
    } else {
      setIsPaused(true);

      if (method === 'pointer') {
        setShowControls(true);
      }
    }
  }

  return (
    <>
      <BasicImage src={isPaused ? coverSrc : src} {...delegated} />
      <PlayButton
        style={{ opacity: showControls ? 1 : 0 }}
        onClick={() => {
          handleTogglePlaying('pointer');
        }}
        onKeyDown={(event) => {
          event.preventDefault();
          if (event.code === 'Enter' || event.code === 'Space') {
            handleTogglePlaying('keyboard');
          }
        }}
        onFocus={() => setShowControls(true)}
        onBlur={() => {
          if (!isPaused) {
            setShowControls(false);
          }
        }}
      >
        <IconWrapper>
          {isPaused ? (
            <Play
              size={20}
              // Optical alignment
              style={{ transform: 'translateX(2px)' }}
            />
          ) : (
            <Pause size={20} />
          )}
        </IconWrapper>
      </PlayButton>
    </>
  );
}

const PlayButton = styled.button`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  padding: 8px;
  background: hsl(0deg 0% 0% / 0.3);
  color: white;
  transition: opacity 400ms;
  border-radius: 4px;
`;

const IconWrapper = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
  background: hsl(0deg 0% 0% / 0.4);
  border-radius: 1000px;
`;

export default ToggleableGifImage;
